import { Component, type ErrorInfo, type ReactNode } from 'react';
import { t } from '@/i18n';
import { formatCrash, recordCrash, type CrashEntry } from '@/lib/crashLog';

// React 19 unmounts the whole tree on an uncaught render error, leaving a blank page and no trace. This keeps the
// shell alive, shows what went wrong (copyable) and records it, so a crash on a deployed build can be reported.
interface Props {
  children: ReactNode;
  /** Changes here (the route) reset the boundary, so navigating away recovers without a reload. */
  resetKey?: string;
}
interface State {
  crash: CrashEntry | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { crash: null };

  static getDerivedStateFromError(error: unknown): State {
    return { crash: recordCrash('render', error) };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    if (this.state.crash && info.componentStack) this.state.crash.stack = `${this.state.crash.stack ?? ''}\n--- components ---${info.componentStack}`.slice(0, 6000);
  }

  componentDidUpdate(prev: Props) {
    if (this.state.crash && prev.resetKey !== this.props.resetKey) this.setState({ crash: null });
  }

  render() {
    const { crash } = this.state;
    if (!crash) return this.props.children;
    const text = formatCrash(crash);
    return (
      <div className="page" style={{ paddingTop: 40 }}>
        <h2 className="serif" style={{ fontSize: 30, fontWeight: 500, margin: 0 }}>{t('crash.title')}</h2>
        <p className="muted" style={{ margin: '10px 0 18px', lineHeight: 1.6, maxWidth: 640 }}>{t('crash.lead')}</p>
        <pre className="crashText">{text}</pre>
        <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          <button type="button" className="btn primary" onClick={() => void navigator.clipboard?.writeText(text)}>{t('crash.copy')}</button>
          <a className="btn quiet" href="/">{t('crash.home')}</a>
          <button type="button" className="btn quiet" onClick={() => location.reload()}>{t('crash.reload')}</button>
        </div>
      </div>
    );
  }
}
